import { HarnessEvent, HarnessEventHandler, LessonAction, LessonState } from "./harness.types";

/**
 * The Classroom Harness: single orchestrator of the lesson experience.
 *
 * - Owns the lesson state machine (see LessonState).
 * - Is the ONLY thing every engine talks to (event bus, not peer-to-peer).
 * - Walks the AI-generated action list one action at a time, deciding
 *   what runs next based on engine callbacks (voice finished a phrase,
 *   whiteboard finished a stroke, student answered a question, etc).
 *
 * This class intentionally has no rendering logic and no direct API
 * calls — those live in the engines. The Harness only sequences.
 */
export class ClassroomHarness {
  private state: LessonState = "idle";
  private handlers = new Set<HarnessEventHandler>();
  private actionQueue: LessonAction[] = [];
  private cursor = 0;

  getState() {
    return this.state;
  }

  subscribe(handler: HarnessEventHandler) {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  private setState(next: LessonState) {
    this.state = next;
    this.emit({ source: "lesson", type: "state:changed", payload: next, timestamp: Date.now() });
  }

  private emit(event: HarnessEvent) {
    this.handlers.forEach((h) => h(event));
  }

  /** Called once the AI Engine returns a full action list for this lesson step. */
  loadActions(actions: LessonAction[]) {
    this.actionQueue = actions;
    this.cursor = 0;
    this.setState("planning");
  }

  /**
   * Advances to the next action. Engines call this when they've finished
   * their part of the CURRENT action (voice finished speaking, whiteboard
   * finished a stroke, etc) — the Harness never assumes fixed durations.
   */
  advance() {
    const action = this.actionQueue[this.cursor];
    if (!action) {
      this.setState("complete");
      return;
    }

    switch (action.type) {
      case "speak":
        this.setState("speaking");
        break;
      case "write":
      case "draw":
      case "highlight":
        this.setState("writing");
        break;
      case "question":
        this.setState("questioning");
        break;
      case "wait_for_answer":
        this.setState("waiting_for_answer");
        break;
      default:
        this.setState("transitioning");
    }

    this.emit({ source: "lesson", type: "action:dispatched", payload: action, timestamp: Date.now() });
    this.cursor += 1;
  }

  /** Student submitted an answer — routes to evaluation, not directly to AI. */
  submitAnswer(answer: string) {
    this.setState("evaluating");
    this.emit({ source: "student", type: "answer:submitted", payload: answer, timestamp: Date.now() });
  }
}
